import type { History, SchemaEntry, View } from '../engine/types'
import { monthKeyFromT } from '../engine/calendar'
import { indexSchema, parseNumeric } from '../engine/schema'
import { gridDisplayUnit } from '../gridClasses'
import type { SnapshotRow } from '@/services/projections'

const ALL_VIEWS: View[] = ['river_oaks', 'highland_park', 'ga', 'consolidated']

function isView(value: string): value is View {
  return (ALL_VIEWS as string[]).includes(value)
}

function coerceHistoryNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') {
    return null
  }
  const parsed = parseNumeric(raw)
  return typeof parsed === 'number' ? parsed : null
}

function coerceProjectedNumber(raw: string | undefined): number | null {
  if (raw === undefined) {
    return null
  }
  const parsed = parseNumeric(raw)
  return typeof parsed === 'number' ? parsed : null
}

function deltaPctForLine(
  delta: number | null,
  projected: number | null,
  lineKey: string,
  schemaByKey: Record<string, SchemaEntry>,
): number | null {
  if (delta === null || projected === null || projected === 0) {
    return null
  }
  const entry = schemaByKey[lineKey]
  const unit = entry ? gridDisplayUnit(lineKey, entry.unit) : 'usd'
  if (unit === 'pct') {
    return null
  }
  return delta / Math.abs(projected)
}

function findHistoryT(
  snapshotMonth: string,
  lastClosedMonth: string,
  nHistory: number,
): number | null {
  for (let t = 0; t >= -(nHistory - 1); t -= 1) {
    if (monthKeyFromT(lastClosedMonth, t) === snapshotMonth) {
      return t
    }
  }
  return null
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1]! + sorted[mid]!) / 2
  }
  return sorted[mid]!
}

export type CompareCell = {
  projected: number | null
  actual: number | null
  delta: number | null
  deltaPct: number | null
  driver: { active: string | null; value: number | null; params: Record<string, unknown> | null } | null
}

export type CompareModel = {
  byMonth: Record<string, Record<View, Record<string, CompareCell>>>
  months: string[]
  source: { caseSavedAt: string | null; capturedAt: string | null }
}

export function buildCompareModel(
  snapshots: SnapshotRow[],
  history: History,
  lastClosedMonth: string,
  schema: SchemaEntry[],
): CompareModel {
  const { byKey: schemaByKey } = indexSchema(schema)
  const byMonth: CompareModel['byMonth'] = {}
  const monthSet = new Set<string>()

  for (const snap of snapshots) {
    const t = findHistoryT(snap.month, lastClosedMonth, history.nHistory)
    if (t === null) {
      continue
    }
    const index = history.nHistory - 1 + t
    monthSet.add(snap.month)

    if (!byMonth[snap.month]) {
      byMonth[snap.month] = {} as Record<View, Record<string, CompareCell>>
    }

    const valueViews = snap.values ? Object.keys(snap.values).filter(isView) : []
    const driverViews = snap.driver_inputs ? Object.keys(snap.driver_inputs).filter(isView) : []
    const views =
      valueViews.length > 0
        ? [...new Set([...valueViews, ...driverViews])]
        : driverViews.length > 0
          ? driverViews
          : ALL_VIEWS.filter((view) => snap.values?.[view])

    for (const view of views) {
      if (!byMonth[snap.month]![view]) {
        byMonth[snap.month]![view] = {}
      }
      const lineKeys = new Set<string>()
      for (const key of Object.keys(snap.values?.[view] ?? {})) {
        lineKeys.add(key)
      }
      for (const key of Object.keys(snap.driver_inputs?.[view] ?? {})) {
        lineKeys.add(key)
      }

      for (const lineKey of lineKeys) {
        const projected = coerceProjectedNumber(snap.values?.[view]?.[lineKey])
        const actualRaw = history.values[view]?.[lineKey]?.[index]
        const actual = coerceHistoryNumber(actualRaw)
        const delta =
          actual !== null && projected !== null ? actual - projected : null
        const deltaPct = deltaPctForLine(delta, projected, lineKey, schemaByKey)

        const driverRaw = snap.driver_inputs?.[view]?.[lineKey]
        let driver: CompareCell['driver'] = null
        if (driverRaw) {
          const driverValue = coerceProjectedNumber(driverRaw.value ?? undefined)
          driver = {
            active: driverRaw.active,
            value: driverValue,
            params: driverRaw.params,
          }
        }

        byMonth[snap.month]![view]![lineKey] = {
          projected,
          actual,
          delta,
          deltaPct,
          driver,
        }
      }
    }
  }

  const months = [...monthSet].sort()
  const latestMonth = months.at(-1)
  const latestSnap =
    latestMonth != null
      ? snapshots.filter((s) => s.month === latestMonth).at(-1) ?? null
      : null

  return {
    byMonth,
    months,
    source: {
      caseSavedAt: latestSnap?.case_saved_at ?? null,
      capturedAt: latestSnap?.captured_at ?? null,
    },
  }
}

export function monthAccuracy(
  model: CompareModel,
  view: View,
  month: string,
  keys: string[],
): {
  linesCompared: number
  medianAbsPct: number | null
  largestMiss: { lineKey: string; delta: number; deltaPct: number | null } | null
} {
  const compared: { lineKey: string; delta: number; deltaPct: number | null }[] = []
  for (const lineKey of keys) {
    const cell = model.byMonth[month]?.[view]?.[lineKey]
    if (!cell || cell.actual === null || cell.projected === null || cell.delta === null) {
      continue
    }
    compared.push({ lineKey, delta: cell.delta, deltaPct: cell.deltaPct })
  }

  const linesCompared = compared.length
  if (linesCompared === 0) {
    return { linesCompared: 0, medianAbsPct: null, largestMiss: null }
  }

  const pctAbsValues = compared
    .map((c) => (c.deltaPct !== null ? Math.abs(c.deltaPct) : null))
    .filter((v): v is number => v !== null)
  const medianAbsPct = pctAbsValues.length > 0 ? median(pctAbsValues) : null

  let largestMiss: { lineKey: string; delta: number; deltaPct: number | null } | null = null
  let bestScore = -1
  const anyPct = compared.some((c) => c.deltaPct !== null)
  for (const lineKey of keys) {
    const row = compared.find((c) => c.lineKey === lineKey)
    if (!row) {
      continue
    }
    const score = anyPct
      ? row.deltaPct !== null
        ? Math.abs(row.deltaPct)
        : -1
      : Math.abs(row.delta)
    if (score < 0) {
      continue
    }
    if (score > bestScore) {
      bestScore = score
      largestMiss = row
    }
  }

  return { linesCompared, medianAbsPct, largestMiss }
}

export function multiMonthAccuracy(
  model: CompareModel,
  view: View,
  keys: string[],
): Record<
  string,
  { months: number; meanPct: number | null; meanAbsPct: number | null; worstMonth: string | null }
> {
  const result: Record<
    string,
    { months: number; meanPct: number | null; meanAbsPct: number | null; worstMonth: string | null }
  > = {}

  for (const lineKey of keys) {
    const rows: { month: string; delta: number; deltaPct: number | null }[] = []
    for (const month of model.months) {
      const cell = model.byMonth[month]?.[view]?.[lineKey]
      if (!cell || cell.actual === null || cell.projected === null || cell.delta === null) {
        continue
      }
      rows.push({ month, delta: cell.delta, deltaPct: cell.deltaPct })
    }
    if (rows.length === 0) {
      continue
    }

    const pcts = rows.map((r) => r.deltaPct).filter((v): v is number => v !== null)
    const meanPct = pcts.length > 0 ? pcts.reduce((a, b) => a + b, 0) / pcts.length : null
    const meanAbsPct =
      pcts.length > 0 ? pcts.reduce((a, b) => a + Math.abs(b), 0) / pcts.length : null

    const anyPct = pcts.length > 0
    let worstMonth: string | null = null
    let worstScore = -1
    for (const row of rows) {
      const score = anyPct
        ? row.deltaPct !== null
          ? Math.abs(row.deltaPct)
          : -1
        : Math.abs(row.delta)
      if (score < 0) {
        continue
      }
      if (score > worstScore) {
        worstScore = score
        worstMonth = row.month
      }
    }

    result[lineKey] = {
      months: rows.length,
      meanPct,
      meanAbsPct,
      worstMonth,
    }
  }

  return result
}
